import { apiRequest } from './client';
import {
  CreatePipeBody,
  CreateProjectBody,
  MicrosoftTargetConfig,
  Pipe,
  PipeSettings,
  PipeStatus,
  PipeSyncQueuedResponse,
  ProcorePdfSize,
  ProcoreWebhookStatusResponse,
  Project,
  ProjectDataTableResponse,
  ProjectMember,
  ProjectNotificationPolicy,
  ProjectSettings,
  ProjectStatus,
  SaveMatchedColumnsBody,
  SmartsheetTargetConfig,
  UpdatePipeBody,
  UUID,
  WebhookConfig,
  WebhookState,
} from './types';

const DAY_STR_TO_NUM: Record<string, number> = {
  sun: 0,
  sunday: 0,
  mon: 1,
  monday: 1,
  tue: 2,
  tuesday: 2,
  wed: 3,
  wednesday: 3,
  thu: 4,
  thursday: 4,
  fri: 5,
  friday: 5,
  sat: 6,
  saturday: 6,
};

function normalizeWorkingDays(raw: unknown): number[] {
  if (!Array.isArray(raw)) {
    return [1, 2, 3, 4, 5];
  }
  const days: number[] = [];
  for (const item of raw) {
    if (typeof item === 'number' && item >= 0 && item <= 6) {
      if (!days.includes(item)) days.push(item);
    } else if (typeof item === 'string') {
      const mapped = DAY_STR_TO_NUM[item.trim().toLowerCase()];
      if (mapped !== undefined && !days.includes(mapped)) {
        days.push(mapped);
      }
    }
  }
  return days.sort((a, b) => a - b);
}

function normalizeProjectStatus(rawStatus: unknown): ProjectStatus {
  const s = typeof rawStatus === 'string' ? rawStatus.toLowerCase() : '';
  if (s === 'active' || s === 'connected') return 'connected';
  if (s === 'disabled') return 'disabled';
  if (s === 'sync_failed' || s === 'errored') return 'sync_failed';
  if (s === 'not_configured') return 'not_configured';
  return 'connected';
}

function normalizeWebhookStatus(rawStatus: unknown, hookId: unknown): WebhookState {
  const s = typeof rawStatus === 'string' ? rawStatus.toLowerCase() : '';
  if (s === 'active' || s === 'enabled') return 'active';
  if (s === 'failed' || s === 'error') return 'failed';
  if (hookId) return 'active';
  return 'not_installed';
}

function normalizePipeStatus(rawStatus: unknown, rawState: unknown): PipeStatus {
  const s = String(rawStatus ?? rawState ?? '').toLowerCase();
  if (s === 'syncing' || s === 'connected' || s === 'active') return 'connected';
  if (s === 'configuring' || s === 'not_configured') return 'not_configured';
  if (s === 'paused' || s === 'disabled' || s === 'archived') return 'disabled';
  if (s === 'errored' || s === 'sync_failed') return 'sync_failed';
  if (s === 'limit_exceeded') return 'limit_exceeded';
  return 'not_configured';
}

export function normalizeProject(raw: Project & Record<string, unknown>): Project {
  if (!raw || typeof raw !== 'object') return raw;
  const existingSettings = (raw.settings as Partial<ProjectSettings> | undefined) ?? {};
  const existingWebhook = (raw.webhook as Partial<WebhookConfig> | undefined) ?? {};

  const isEnableNotifications =
    typeof raw.is_enable_notifications === 'boolean' ? raw.is_enable_notifications : true;
  const defaultPolicy: ProjectNotificationPolicy = isEnableNotifications
    ? 'send_once_per_day'
    : 'disable_notification';

  const rawHolidays = existingSettings.holidays ?? raw.holidays;
  const holidays = Array.isArray(rawHolidays)
    ? rawHolidays.filter((h): h is string => typeof h === 'string')
    : [];

  const rawEmails = existingSettings.additional_emails ?? raw.notification_cc_emails;
  const additionalEmails = Array.isArray(rawEmails)
    ? rawEmails.filter((e): e is string => typeof e === 'string')
    : [];

  const hookIdRaw = existingWebhook.hook_id ?? raw.procore_webhook_id;
  const hookId = hookIdRaw !== undefined && hookIdRaw !== null ? String(hookIdRaw) : null;
  const rawTriggers = existingWebhook.triggers ?? raw.procore_webhook_triggers;
  const triggers = Array.isArray(rawTriggers)
    ? rawTriggers.filter((t): t is string => typeof t === 'string')
    : [];

  const reason =
    (raw.status_change_reason as string | null | undefined) ??
    (raw.status_Change_reason as string | null | undefined) ??
    (raw.disabled_reason as string | null | undefined) ??
    null;

  return {
    ...raw,
    procore_company_id: Number(raw.procore_company_id ?? 0),
    procore_project_id: Number(raw.procore_project_id ?? 0),
    smartsheet_workspace_id:
      raw.smartsheet_workspace_id !== undefined && raw.smartsheet_workspace_id !== null
        ? Number(raw.smartsheet_workspace_id)
        : null,
    status: normalizeProjectStatus(raw.status),
    status_Change_reason: reason,
    status_change_reason: reason,
    subscription_status:
      raw.subscription_status === 'cancelled' || raw.is_subscription_End === true
        ? 'cancelled'
        : 'active',
    is_separate_invoice: Boolean(raw.is_separate_invoice),
    settings: {
      working_days: normalizeWorkingDays(existingSettings.working_days ?? raw.working_days),
      holidays,
      allow_nested_tasks:
        typeof existingSettings.allow_nested_tasks === 'boolean'
          ? existingSettings.allow_nested_tasks
          : Boolean(raw.allow_nested_tasks),
      update_tasks_status:
        typeof existingSettings.update_tasks_status === 'boolean'
          ? existingSettings.update_tasks_status
          : Boolean(raw.update_tasks_status),
      notification_policy: existingSettings.notification_policy ?? defaultPolicy,
      additional_emails: additionalEmails,
    },
    webhook: {
      hook_id: hookId,
      namespace:
        (existingWebhook.namespace as string | null | undefined) ??
        (raw.procore_webhook_namespace as string | null | undefined) ??
        null,
      status: normalizeWebhookStatus(
        existingWebhook.status ?? raw.procore_webhook_status,
        hookId,
      ),
      triggers,
    },
  };
}

export function normalizePipe(raw: Pipe & Record<string, unknown>): Pipe {
  if (!raw || typeof raw !== 'object') return raw;
  const existingSettings = (raw.settings as Partial<PipeSettings> | undefined) ?? {};
  const existingSm = (raw.smartsheet as Partial<SmartsheetTargetConfig> | undefined) ?? {};
  const existingMs = (raw.microsoft as Partial<MicrosoftTargetConfig> | undefined) ?? {};

  const rawSheetId = existingSm.sheet_id ?? raw.sm_sheet_id;
  const sheetId =
    rawSheetId !== undefined && rawSheetId !== null && Number(rawSheetId) > 0
      ? Number(rawSheetId)
      : null;

  const rawWebhookId = existingSm.webhook_id ?? raw.sm_webhook_id;
  const webhookId =
    rawWebhookId !== undefined && rawWebhookId !== null && Number(rawWebhookId) > 0
      ? Number(rawWebhookId)
      : null;

  const rawColumnMap = existingSm.column_map ?? raw.sm_column_map;
  const smColumnMap =
    rawColumnMap && typeof rawColumnMap === 'object' && !Array.isArray(rawColumnMap)
      ? (rawColumnMap as Record<string, unknown>)
      : {};

  const rawMsColumnMap = existingMs.column_map ?? raw.ms_column_map;
  const msColumnMap =
    rawMsColumnMap && typeof rawMsColumnMap === 'object' && !Array.isArray(rawMsColumnMap)
      ? (rawMsColumnMap as Record<string, unknown>)
      : {};

  return {
    ...raw,
    status: normalizePipeStatus(raw.status, raw.state),
    status_change_reason:
      (raw.status_change_reason as string | null | undefined) ??
      (raw.disabled_reason as string | null | undefined) ??
      null,
    is_locked: Boolean(raw.is_locked),
    settings: {
      use_schedule_chart: Boolean(
        existingSettings.use_schedule_chart ?? raw.use_schedule_chart ?? false,
      ),
      colors_coding_enabled: Boolean(
        existingSettings.colors_coding_enabled ?? raw.colors_coding_enabled ?? false,
      ),
      summary_tasks_enabled: Boolean(
        existingSettings.summary_tasks_enabled ?? raw.summary_tasks_enabled ?? true,
      ),
      procore_folder_id:
        existingSettings.procore_folder_id !== undefined &&
        existingSettings.procore_folder_id !== null
          ? Number(existingSettings.procore_folder_id)
          : raw.procore_folder_id !== undefined && raw.procore_folder_id !== null
            ? Number(raw.procore_folder_id)
            : null,
      procore_file_id:
        existingSettings.procore_file_id !== undefined &&
        existingSettings.procore_file_id !== null
          ? Number(existingSettings.procore_file_id)
          : raw.procore_file_id !== undefined && raw.procore_file_id !== null
            ? Number(raw.procore_file_id)
            : null,
      procore_pdf_size: ((existingSettings.procore_pdf_size ??
        raw.procore_pdf_size ??
        'A2') as ProcorePdfSize),
    },
    smartsheet: {
      sheet_id: sheetId,
      sheet_name:
        (existingSm.sheet_name as string | null | undefined) ??
        (raw.sm_sheet_name as string | null | undefined) ??
        null,
      permalink:
        (existingSm.permalink as string | null | undefined) ??
        (raw.sm_permalink as string | null | undefined) ??
        null,
      webhook_id: webhookId,
      webhook_status:
        (existingSm.webhook_status as string | null | undefined) ??
        (raw.sm_webhook_status as string | null | undefined) ??
        null,
      webhook_enabled_at:
        (existingSm.webhook_enabled_at as string | null | undefined) ??
        (raw.sm_webhook_enabled_at as string | null | undefined) ??
        null,
      column_map: smColumnMap,
    },
    microsoft: {
      project_id:
        (existingMs.project_id as string | null | undefined) ??
        (raw.ms_project_id as string | null | undefined) ??
        null,
      project_name:
        (existingMs.project_name as string | null | undefined) ??
        (raw.ms_project_name as string | null | undefined) ??
        null,
      subscription_id:
        (existingMs.subscription_id as string | null | undefined) ??
        (raw.ms_subscription_id as string | null | undefined) ??
        null,
      column_map: msColumnMap,
    },
    last_sync_at: (raw.last_sync_at as string | null | undefined) ?? null,
  };
}

export const projectsApi = {
  // --------------------------------------------------------------------------
  // Projects CRUD & Settings (`/ms-project/projects*`)
  // --------------------------------------------------------------------------
  async listProjects(companyId?: UUID): Promise<Project[]> {
    const rows = await apiRequest<Array<Project & Record<string, unknown>>>('/ms-project/projects', {
      query: companyId ? { company_id: companyId } : undefined,
    });
    return Array.isArray(rows) ? rows.map(normalizeProject) : [];
  },

  async getActiveProjects(companyId?: UUID): Promise<Project[]> {
    const rows = await apiRequest<Array<Project & Record<string, unknown>>>(
      '/ms-project/get-active-projects',
      {
        query: companyId ? { company_id: companyId } : undefined,
      },
    );
    return Array.isArray(rows) ? rows.map(normalizeProject) : [];
  },

  async getProject(projectId: UUID): Promise<Project> {
    const row = await apiRequest<Project & Record<string, unknown>>(
      `/ms-project/projects/${projectId}`,
    );
    return normalizeProject(row);
  },

  async createProject(body: CreateProjectBody): Promise<Project> {
    const row = await apiRequest<Project & Record<string, unknown>>('/ms-project/projects', {
      method: 'POST',
      body,
    });
    return normalizeProject(row);
  },

  deleteProject(projectId: UUID): Promise<void> {
    return apiRequest<void>(`/ms-project/projects/${projectId}`, {
      method: 'DELETE',
    });
  },

  async updateProjectSettings(projectId: UUID, settings: ProjectSettings): Promise<Project> {
    const row = await apiRequest<Project & Record<string, unknown>>(
      '/ms-project/projects/update-settings',
      {
        method: 'POST',
        body: {
          project_id: projectId,
          settings,
        },
      },
    );
    return normalizeProject(row);
  },

  getProjectPbrUser(projectId: UUID): Promise<{ project_id: UUID; billing_user_id: UUID | null }> {
    return apiRequest<{ project_id: UUID; billing_user_id: UUID | null }>(
      '/ms-project/get-pbr-user',
      {
        query: { project_id: projectId },
      },
    );
  },

  async setProjectPbrUser(projectId: UUID, billingUserId: UUID | null): Promise<Project> {
    const row = await apiRequest<Project & Record<string, unknown>>('/ms-project/set-pbr-user', {
      method: 'POST',
      body: {
        project_id: projectId,
        billing_user_id: billingUserId,
      },
    });
    return normalizeProject(row);
  },

  async setSeparateInvoiceState(projectId: UUID, isSeparateInvoice: boolean): Promise<Project> {
    const row = await apiRequest<Project & Record<string, unknown>>(
      '/ms-project/set-separate-invoice-state',
      {
        method: 'POST',
        body: {
          project_id: projectId,
          is_separate_invoice: isSeparateInvoice,
        },
      },
    );
    return normalizeProject(row);
  },

  // --------------------------------------------------------------------------
  // Project Members (`/ms-project/projects/{id}/members`)
  // --------------------------------------------------------------------------
  listProjectMembers(projectId: UUID): Promise<ProjectMember[]> {
    return apiRequest<ProjectMember[]>(`/ms-project/projects/${projectId}/members`);
  },

  upsertProjectMember(
    projectId: UUID,
    userId: UUID,
    allowEditGanttChart = true,
  ): Promise<ProjectMember> {
    return apiRequest<ProjectMember>(`/ms-project/projects/${projectId}/members`, {
      method: 'POST',
      body: {
        user_id: userId,
        allow_edit_gantt_chart: allowEditGanttChart,
      },
    });
  },

  removeProjectMember(projectId: UUID, userId: UUID): Promise<void> {
    return apiRequest<void>(`/ms-project/projects/${projectId}/members/${userId}`, {
      method: 'DELETE',
    });
  },

  // --------------------------------------------------------------------------
  // Connected External IDs & Admin Data Table
  // --------------------------------------------------------------------------
  getConnectedProcoreProjectIds(companyId?: UUID): Promise<number[]> {
    return apiRequest<number[]>('/ms-project/get-connected-procore-projects-ids', {
      query: companyId ? { company_id: companyId } : undefined,
    });
  },

  getConnectedSmartsheetSheetIds(companyId?: UUID): Promise<number[]> {
    return apiRequest<number[]>('/ms-project/get-connected-smartsheet-sheets-ids', {
      query: companyId ? { company_id: companyId } : undefined,
    });
  },

  getConnectedMicrosoftProjectIds(companyId?: UUID): Promise<string[]> {
    return apiRequest<string[]>('/ms-project/get-connected-microsoft-projects-ids', {
      query: companyId ? { company_id: companyId } : undefined,
    });
  },

  async getProjectsDataTable(
    page = 1,
    pageSize = 25,
    companyId?: UUID,
    search?: string,
  ): Promise<ProjectDataTableResponse<Project>> {
    const res = await apiRequest<ProjectDataTableResponse<Project & Record<string, unknown>>>(
      '/ms-project/data-table',
      {
        query: {
          page,
          page_size: pageSize,
          company_id: companyId,
          search,
        },
        scope: 'admin',
      },
    );
    return {
      ...res,
      data: Array.isArray(res.data) ? res.data.map(normalizeProject) : [],
    };
  },

  // --------------------------------------------------------------------------
  // Procore Project Webhooks (`/ms-project/projects/*procore*webhook*`)
  // --------------------------------------------------------------------------
  getProcoreWebhookStatus(projectId: UUID): Promise<ProcoreWebhookStatusResponse> {
    return apiRequest<ProcoreWebhookStatusResponse>(
      '/ms-project/projects/procore-webhook-enabled',
      {
        query: { project_id: projectId },
      },
    );
  },

  async createProcoreWebhook(projectId: UUID): Promise<Project> {
    const row = await apiRequest<Project & Record<string, unknown>>(
      '/ms-project/projects/create-procore-project-webhook',
      {
        method: 'POST',
        body: { project_id: projectId },
      },
    );
    return normalizeProject(row);
  },

  async removeProcoreWebhook(projectId: UUID): Promise<Project> {
    const row = await apiRequest<Project & Record<string, unknown>>(
      '/ms-project/projects/remove-procore-project-webhook',
      {
        method: 'POST',
        body: { project_id: projectId },
      },
    );
    return normalizeProject(row);
  },

  async addProcoreWebhookTriggers(projectId: UUID, triggers: string[]): Promise<Project> {
    const row = await apiRequest<Project & Record<string, unknown>>(
      '/ms-project/projects/add-procore-webhook-triggers',
      {
        method: 'POST',
        body: { project_id: projectId, triggers },
      },
    );
    return normalizeProject(row);
  },

  async removeProcoreWebhookTriggers(projectId: UUID, triggers: string[]): Promise<Project> {
    const row = await apiRequest<Project & Record<string, unknown>>(
      '/ms-project/projects/remove-procore-webhook-triggers',
      {
        method: 'POST',
        body: { project_id: projectId, triggers },
      },
    );
    return normalizeProject(row);
  },

  // --------------------------------------------------------------------------
  // Pipes CRUD, Column Matching & Sync Trigger (`/ms-project/pipes*`)
  // --------------------------------------------------------------------------
  async listProjectPipes(projectId: UUID): Promise<Pipe[]> {
    const rows = await apiRequest<Array<Pipe & Record<string, unknown>>>(
      `/ms-project/projects/${projectId}/pipes`,
    );
    return Array.isArray(rows) ? rows.map(normalizePipe) : [];
  },

  async getPipe(pipeId: UUID): Promise<Pipe> {
    const row = await apiRequest<Pipe & Record<string, unknown>>(`/ms-project/pipes/${pipeId}`);
    return normalizePipe(row);
  },

  async createPipe(body: CreatePipeBody): Promise<Pipe> {
    const row = await apiRequest<Pipe & Record<string, unknown>>('/ms-project/pipes', {
      method: 'POST',
      body,
    });
    return normalizePipe(row);
  },

  async updatePipe(body: UpdatePipeBody): Promise<Pipe> {
    const row = await apiRequest<Pipe & Record<string, unknown>>('/ms-project/pipes/update-pipe', {
      method: 'POST',
      body,
    });
    return normalizePipe(row);
  },

  deletePipe(pipeId: UUID): Promise<void> {
    return apiRequest<void>(`/ms-project/pipes/${pipeId}`, {
      method: 'DELETE',
    });
  },

  async enablePipe(pipeId: UUID): Promise<Pipe> {
    const row = await apiRequest<Pipe & Record<string, unknown>>(
      `/ms-project/pipes/${pipeId}/enable`,
      {
        method: 'POST',
      },
    );
    return normalizePipe(row);
  },

  async disablePipe(pipeId: UUID, reason?: string): Promise<Pipe> {
    const row = await apiRequest<Pipe & Record<string, unknown>>(
      `/ms-project/pipes/${pipeId}/disable`,
      {
        method: 'POST',
        body: reason ? { reason } : {},
      },
    );
    return normalizePipe(row);
  },

  async saveMatchedColumns(body: SaveMatchedColumnsBody): Promise<Pipe> {
    const row = await apiRequest<Pipe & Record<string, unknown>>(
      '/ms-project/save-matched-columns',
      {
        method: 'POST',
        body,
      },
    );
    return normalizePipe(row);
  },

  triggerPipeSync(pipeId: UUID): Promise<PipeSyncQueuedResponse> {
    return apiRequest<PipeSyncQueuedResponse>(`/ms-project/pipes/${pipeId}/sync`, {
      method: 'POST',
    });
  },
};
